import type { Rule } from '@nono/contract';
import { useMutation } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Switch } from '@/components/ui/switch';
import { api, queryClient } from '@/lib/api';
import { type Locale, useLocale, useT, useViewMessages } from '@/lib/i18n';
import { useRules } from '@/lib/queries';
import { cn } from '@/lib/utils';

/*
 * Rules — global (segment-agnostic) list of rule DEFINITIONS. Each card shows
 * the human "healthy when <metric> <≥/≤> <threshold> over <N>d" line, the
 * severity, the breach message + optional recommendation, an enabled Switch,
 * and editable threshold + windowDays inputs. The only writes are PATCH /rules/:id
 * ({ enabled } | { threshold, windowDays }). No metric VALUES, no targets, so no
 * status-tick / tinted-number treatment applies here.
 */

const MESSAGES = {
  nb: {
    title: 'Regler',
    sub: 'Når en måling bryter terskelen over perioden sin, utløses et varsel med en anbefaling.',
    enabledLabel: 'Aktivert',
    healthyWhen: 'Sunn når',
    overWindow: (days: number) => `over ${days}d`,
    recommendationPrefix: '→ ',
    severityCritical: 'kritisk',
    severityWarn: 'advarsel',
    severityInfo: 'info',
    empty: 'Ingen regler er satt opp ennå.',
    toggleError: 'Kunne ikke oppdatere regelen. Prøv igjen.',
    thresholdLabel: 'Terskel',
    windowLabel: 'Periode (dager)',
    save: 'Lagre',
    saving: 'Lagrer …',
    metric: {
      cpa: 'Kostnad per medlem (CAC)',
      surPct: 'Oppmøteandel',
      ccPct: 'Salgsandel (solgt / møtt opp)',
      cpl: 'Kostnad per lead',
      ctr: 'Klikkrate (CTR)',
      rRoi: 'Inntekts-ROI',
    } as Record<string, string>,
  },
  en: {
    title: 'Rules',
    sub: 'When a metric breaches over its window, an alert with a recommendation is raised.',
    enabledLabel: 'Enabled',
    healthyWhen: 'Healthy when',
    overWindow: (days: number) => `over ${days}d`,
    recommendationPrefix: '→ ',
    severityCritical: 'critical',
    severityWarn: 'warn',
    severityInfo: 'info',
    empty: 'No rules configured yet.',
    toggleError: "Couldn't update the rule. Try again.",
    thresholdLabel: 'Threshold',
    windowLabel: 'Window (days)',
    save: 'Save',
    saving: 'Saving …',
    metric: {
      cpa: 'Cost per member (CAC)',
      surPct: 'Show-up rate',
      ccPct: 'Close rate (sold / showed-up)',
      cpl: 'Cost per lead',
      ctr: 'Click-through rate (CTR)',
      rRoi: 'Revenue ROI',
    } as Record<string, string>,
  },
};

type ViewMessages = (typeof MESSAGES)['nb'];

const SEVERITY_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  critical: 'bad',
  warn: 'warn',
  info: 'brand',
};

// Severity left accent. Legacy bucketed info into the amber/warn border; we map
// info → blue accent here so the accent matches its (blue) pill — a deliberate
// divergence from legacy noted in the spec's designMapping.
const SEVERITY_ACCENT: Record<string, string> = {
  critical: 'border-l-bad',
  warn: 'border-l-warn',
  info: 'border-l-brand',
};

// The threshold is a displayed number, so its decimal separator follows the
// active locale (nb: 12,5 · en: 12.5). Built once per locale, not per render.
const NUM_FMT: Record<Locale, Intl.NumberFormat> = {
  nb: new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
  en: new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
};

export function RulesPage() {
  const m = useViewMessages(MESSAGES);
  const rulesQuery = useRules();

  if (rulesQuery.isLoading)
    return (
      <PageFrame m={m}>
        <LoadingState />
      </PageFrame>
    );
  if (rulesQuery.isError || !rulesQuery.data)
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => rulesQuery.refetch()} />
      </PageFrame>
    );

  const rules = rulesQuery.data.rules;

  return (
    <PageFrame m={m}>
      {rules.length === 0 ? (
        <EmptyState title={m.empty} />
      ) : (
        <div className="flex flex-col gap-3">
          {rules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} m={m} />
          ))}
        </div>
      )}
    </PageFrame>
  );
}

function PageFrame({ m, children }: { m: ViewMessages; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="flex items-center gap-2 text-title text-fg">
          {m.title}
          <CaveatRef id="rulesWindowBlended" />
        </h1>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
      </header>
      {children}
    </div>
  );
}

function RuleCard({ rule, m }: { rule: Rule; m: ViewMessages }) {
  const t = useT();
  const { locale } = useLocale();
  const [error, setError] = useState(false);
  const [threshold, setThreshold] = useState(String(rule.threshold));
  const [windowDays, setWindowDays] = useState(String(rule.windowDays));

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.patchRule(rule.id, { enabled }),
    onMutate: () => setError(false),
    onError: () => setError(true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });

  const edit = useMutation({
    mutationFn: (body: { threshold: number; windowDays: number }) => api.patchRule(rule.id, body),
    onMutate: () => setError(false),
    onError: () => setError(true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });

  const dir = rule.comparator === 'gte' ? '≥' : '≤';
  const metricLabel = m.metric[rule.metricKey] ?? rule.metricKey;
  const tone = SEVERITY_TONE[rule.severity] ?? 'neutral';
  const accent = SEVERITY_ACCENT[rule.severity] ?? 'border-l-warn';
  const severityLabel = severityWord(rule.severity, m);

  const parsedThreshold = Number(threshold);
  const parsedWindow = Number(windowDays);
  const dirty = parsedThreshold !== rule.threshold || parsedWindow !== rule.windowDays;
  const valid =
    threshold.trim() !== '' &&
    windowDays.trim() !== '' &&
    Number.isFinite(parsedThreshold) &&
    Number.isInteger(parsedWindow) &&
    parsedWindow >= 1;

  const busy = toggle.isPending || edit.isPending;

  return (
    <Card
      className={cn(
        'border-l-4 p-3 transition-fast sm:p-4',
        accent,
        rule.enabled ? '' : 'opacity-60',
      )}
    >
      {/* row 1: switch + name + healthy-when line + severity */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Switch
          checked={rule.enabled}
          disabled={busy}
          onCheckedChange={(next) => toggle.mutate(next)}
          aria-label={`${m.enabledLabel}: ${rule.name}`}
        />
        <span className="text-section font-semibold text-fg">{rule.name}</span>
        <span className="flex min-w-0 flex-wrap items-center gap-1 text-label text-fg-muted">
          <span>{m.healthyWhen}</span>
          <span className="inline-flex items-center gap-1 font-medium text-fg">
            {metricLabel}
            <CaveatRef id="rulesWindowBlended" />
          </span>
          <span aria-hidden>{dir}</span>
          <span className="tabular text-fg">{NUM_FMT[locale].format(rule.threshold)}</span>
          <span>{m.overWindow(rule.windowDays)}</span>
        </span>
        <Badge tone={tone} className="shrink-0 uppercase sm:ml-auto">
          {severityLabel}
        </Badge>
      </div>

      {/* row 2: breach message */}
      <p className="mt-3 text-body text-fg-muted">{rule.message}</p>

      {/* row 3 (optional): recommendation */}
      {rule.recommendation ? (
        <p className="mt-1 text-label text-fg-subtle">
          {m.recommendationPrefix}
          {rule.recommendation}
        </p>
      ) : null}

      {/* row 4: editable threshold + window */}
      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-3">
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-micro text-fg-subtle">{m.thresholdLabel}</span>
          <input
            type="number"
            inputMode="decimal"
            step="any"
            value={threshold}
            disabled={busy}
            onChange={(e) => setThreshold(e.target.value)}
            className="h-10 w-full rounded-md border border-border bg-surface px-2 text-body tabular text-fg transition-fast focus:border-border-strong disabled:opacity-50 sm:h-8 sm:w-28"
          />
        </label>
        <label className="flex w-full flex-col gap-1 sm:w-auto">
          <span className="text-micro text-fg-subtle">{m.windowLabel}</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={windowDays}
            disabled={busy}
            onChange={(e) => setWindowDays(e.target.value)}
            className="h-10 w-full rounded-md border border-border bg-surface px-2 text-body tabular text-fg transition-fast focus:border-border-strong disabled:opacity-50 sm:h-8 sm:w-28"
          />
        </label>
        <Button
          variant="secondary"
          size="sm"
          disabled={!dirty || !valid || busy}
          onClick={() => edit.mutate({ threshold: parsedThreshold, windowDays: parsedWindow })}
        >
          {edit.isPending ? m.saving : m.save}
        </Button>
        {error ? <span className="text-label text-bad-fg">{m.toggleError}</span> : null}
      </div>

      {/* hidden retry affordance reuses the shared retry label for parity with states */}
      <span className="sr-only">{t.retry()}</span>
    </Card>
  );
}

function severityWord(severity: string, m: ViewMessages): string {
  if (severity === 'critical') return m.severityCritical;
  if (severity === 'warn') return m.severityWarn;
  if (severity === 'info') return m.severityInfo;
  return severity;
}
